/**
 * Les vignettes des succès, en cartes de caractères comme le reste du skin.
 *
 * Même principe que `sprites.ts` : un caractère, un pixel logique, dessiné dans
 * un canvas puis agrandi en `image-rendering: pixelated`. Toutes font 16 × 16,
 * et un test le vérifie — une carte plus courte d'une ligne passerait inaperçue
 * à la lecture et décalerait la vignette d'un cran.
 *
 * La palette est celle du jeu, plus les couleurs dont seules les vignettes ont
 * besoin. Steve n'apparaît sur aucune : il est le seul orange du site, et une
 * médaille orange l'aurait imité sans le représenter.
 */

import { SPRITE_PALETTE, measure } from './sprites';

export const BADGE_PALETTE: Readonly<Record<string, string>> = {
  ...SPRITE_PALETTE,
  /** Rouge du cœur. */
  R: '#ff5470',
  /** Or des médailles chiffrées. */
  G: '#ffd166',
  /** Turquoise de la cible — la couleur de la rangée du milieu. */
  T: '#35c2c2',
  /** Bleu très pâle de la bulle. */
  B: '#bfe9ff',
  /** Gris éteint : ce qui est cassé, ou pas encore obtenu. */
  A: '#4a4a70',
  /** Rose de la rangée du haut, pour le poisson sans jambes. */
  N: '#e05f9c',
};

/** Cœur plein — la vague passée sans une égratignure. */
const HEART_ROWS = [
  '................',
  '................',
  '...RRR....RRR...',
  '..RKRRR..RRRRR..',
  '.RKRRRRRRRRRRRR.',
  '.RKRRRRRRRRRRRR.',
  '.RRRRRRRRRRRRRR.',
  '.RRRRRRRRRRRRRR.',
  '..RRRRRRRRRRRR..',
  '...RRRRRRRRRR...',
  '....RRRRRRRR....',
  '.....RRRRRR.....',
  '......RRRR......',
  '.......RR.......',
  '................',
  '................',
];

/** Le même cœur, fendu : il n'en restait qu'un. */
const HEART_CRACKED_ROWS = [
  '................',
  '................',
  '...RRR....AAA...',
  '..RKRRR..AAAAA..',
  '.RKRRRPRAAAAAAA.',
  '.RKRRRRPAAAAAAA.',
  '.RRRRRPRAAAAAAA.',
  '.RRRRRRPAAAAAAA.',
  '..RRRRPRAAAAAA..',
  '...RRRPAAAAAA...',
  '....RRPAAAAA....',
  '.....RPAAAA.....',
  '......PAAA......',
  '.......P........',
  '................',
  '................',
];

/** Chiffre 4. */
const FOUR_ROWS = [
  '................',
  '................',
  '.........GG.....',
  '........GGG.....',
  '.......GGGG.....',
  '......GG.GG.....',
  '.....GG..GG.....',
  '....GG...GG.....',
  '...GG....GG.....',
  '...GGGGGGGGGG...',
  '...GGGGGGGGGG...',
  '.........GG.....',
  '.........GG.....',
  '.........GG.....',
  '................',
  '................',
];

/** Chiffre 8. */
const EIGHT_ROWS = [
  '................',
  '................',
  '....GGGGGGGG....',
  '...GGGGGGGGGG...',
  '...GGG....GGG...',
  '...GGG....GGG...',
  '....GGGGGGGG....',
  '....GGGGGGGG....',
  '...GGG....GGG...',
  '...GGG....GGG...',
  '...GGG....GGG...',
  '...GGGGGGGGGG...',
  '....GGGGGGGG....',
  '................',
  '................',
  '................',
];

/** Cible concentrique — aucune bulle à côté. */
const TARGET_ROWS = [
  '................',
  '..TTTTTTTTTTTT..',
  '..T..........T..',
  '..T.TTTTTTTT.T..',
  '..T.T......T.T..',
  '..T.T.TTTT.T.T..',
  '..T.T.TRRT.T.T..',
  '..T.T.TRRT.T.T..',
  '..T.T.TTTT.T.T..',
  '..T.T......T.T..',
  '..T.TTTTTTTT.T..',
  '..T..........T..',
  '..TTTTTTTTTTTT..',
  '................',
  '................',
  '................',
];

/** Étoile — le score rond. */
const STAR_ROWS = [
  '................',
  '.......GG.......',
  '.......GG.......',
  '......GGGG......',
  '......GGGG......',
  '.GGGGGGGGGGGGGG.',
  '.GGGGGGGGGGGGGG.',
  '..GGGGGGGGGGGG..',
  '...GGGGGGGGGG...',
  '....GGGGGGGG....',
  '...GGGG..GGGG...',
  '...GGG....GGG...',
  '..GGG......GGG..',
  '..GG........GG..',
  '................',
  '................',
];

/** Grosse bulle — celle qu'on n'a pas crachée. */
const BUBBLE_ROWS = [
  '................',
  '.....KKKKKK.....',
  '...KKBBBBBBKK...',
  '..KBBWWBBBBBBK..',
  '.KBBWWWWBBBBBBK.',
  '.KBBWWBBBBBBBBK.',
  '.KBBBBBBBBBBBBK.',
  '.KBBBBBBBBBBBBK.',
  '.KBBBBBBBBBBBBK.',
  '.KBBBBBBBBBBBBK.',
  '..KBBBBBBBBBBK..',
  '...KKBBBBBBKK...',
  '.....KKKKKK.....',
  '................',
  '................',
  '................',
];

/** Un poisson sans jambes, les yeux en croix. */
const FOE_DOWN_ROWS = [
  '................',
  '................',
  '......KKKKK.....',
  '....KKNNNNNKK...',
  '..KKNNNNNNNNNK..',
  '.KNNNNNNNPNPNK..',
  'KKNNNNNNNNPNNNK.',
  'KNNNNNNNNPNPNNK.',
  'KKNNNNNNNNNNNNK.',
  '.KNNNNNNNNNNNK..',
  '..KKNNNNNNNKK...',
  '....KKNNNKK.....',
  '......KKK.......',
  '................',
  '................',
  '................',
];

export const BADGE_SIZE = 16;

export const HEART = measure(HEART_ROWS);
export const HEART_CRACKED = measure(HEART_CRACKED_ROWS);
export const FOUR = measure(FOUR_ROWS);
export const EIGHT = measure(EIGHT_ROWS);
export const TARGET = measure(TARGET_ROWS);
export const STAR = measure(STAR_ROWS);
export const BIG_BUBBLE = measure(BUBBLE_ROWS);
export const FOE_DOWN = measure(FOE_DOWN_ROWS);

/** Toutes les vignettes, pour la vérification de gabarit. */
export const BADGES = [
  HEART,
  HEART_CRACKED,
  FOUR,
  EIGHT,
  TARGET,
  STAR,
  BIG_BUBBLE,
  FOE_DOWN,
] as const;
